import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, Trash2, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import {
  getAllPendingExpenses,
  removePendingExpense,
  updatePendingExpense,
  type PendingExpense,
} from '../../services/offlineDb';
import { formatCurrency } from '../../utils';

/**
 * Persistent banner that appears when one or more offline expenses
 * have permanently failed to sync (4xx from the API).
 *
 * Lets the user inspect which expenses failed, why, and either
 * retry them or discard them.
 */
const FailedExpensesBanner: React.FC = () => {
  const [failed, setFailed] = useState<PendingExpense[]>([]);
  const [expanded, setExpanded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const all = await getAllPendingExpenses();
      setFailed(all.filter((e) => e.status === 'error'));
    } catch {
      // IDB unavailable
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [refresh]);

  const handleDiscard = async (localId: number) => {
    await removePendingExpense(localId);
    await refresh();
  };

  const handleRetry = async (item: PendingExpense) => {
    if (item.localId == null) return;
    item.status = 'pending';
    item.lastError = undefined;
    await updatePendingExpense(item);
    await refresh();
  };

  if (failed.length === 0) return null;

  return (
    <div className="fixed bottom-16 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg">
      <div className="rounded-xl bg-red-50 border border-red-200 shadow-lg overflow-hidden">
        {/* Header */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-center gap-2 px-4 py-2.5 text-red-800 hover:bg-red-100/60 transition-colors"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left text-sm font-semibold">
            {failed.length} offline expense{failed.length > 1 ? 's' : ''} failed to sync
          </span>
          {expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {/* Detail rows */}
        {expanded && (
          <div className="max-h-60 overflow-y-auto divide-y divide-red-100">
            {failed.map((item) => (
              <div
                key={item.localId}
                className="px-4 py-3 flex flex-col gap-1.5"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {item.payload.description || 'Untitled expense'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatCurrency(item.payload.totalAmount)} &middot;{' '}
                      {new Date(item.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleRetry(item)}
                      title="Retry sync"
                      className="rounded-md p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      <RotateCcw className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() =>
                        item.localId != null && handleDiscard(item.localId)
                      }
                      title="Discard expense"
                      className="rounded-md p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {item.lastError && (
                  <p className="text-xs text-red-600 bg-red-100/60 rounded px-2 py-1">
                    {item.lastError}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FailedExpensesBanner;
